import { useQuery } from "@tanstack/react-query";
import React, { createContext, useContext, useMemo } from "react";

import { PaymentsSubscription } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { EXPECTS_FORBIDDEN_META } from "~/configs/queryClient";
import { useTeam } from "~/contexts/TeamProvider";
import { useFeatureFlag } from "~/featureFlags";
import { StatusErrorLike, tryCatch } from "~/helpers/tryCatch";

type BillingAccessValue = {
  isBillingAuthorized: boolean;
  // The backend refused it (403: non-admins). Unlike !isBillingAuthorized,
  // it stays false while the access is being (re)checked and on other
  // errors, which don't make an admin a member.
  isBillingDenied: boolean;
  isLoading: boolean;
  subscription?: PaymentsSubscription | null;
};

const BILLING_FEATURE = "billing";

const BillingAccessContext = createContext<BillingAccessValue | null>(null);

export const useBillingAccess = () => {
  const ctx = useContext(BillingAccessContext);

  if (!ctx) {
    throw new Error(
      "useBillingAccess must be used within BillingAccessProvider",
    );
  }

  return ctx;
};

export const BillingAccessProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { teamId } = useTeam();
  const billingEnabled = useFeatureFlag(BILLING_FEATURE);

  const {
    data: subscription,
    isFetching,
    isError,
    error,
  } = useQuery({
    enabled: teamId != null && billingEnabled,
    queryKey: ["subscription", teamId],
    // Members get 403: that's how the access is told.
    meta: EXPECTS_FORBIDDEN_META,
    queryFn: async () => {
      const result = await tryCatch(apiClient.billing.subscriptionList());

      if (result.error) {
        // no subscription is a valid scenario
        if (result.error.status === 404) {
          return null;
        }

        throw result.error;
      }

      return result?.data?.data?.["payments/subscription"];
    },
  });

  const value = useMemo<BillingAccessValue>(() => {
    const isBillingAuthorized = !isFetching && !isError;

    return {
      isBillingAuthorized,
      isBillingDenied: (error as StatusErrorLike | null)?.status === 403,
      isLoading: isFetching,
      subscription,
    };
  }, [subscription, isFetching, isError, error]);

  return (
    <BillingAccessContext.Provider value={value}>
      {children}
    </BillingAccessContext.Provider>
  );
};
